import { Box } from '@mui/material'
import { RefObject, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import {
  DmRoomDetailQuery,
  DmRoomMessageBoxFragment,
} from '@/clients/utoniq-core/schema'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { Organism } from '@/core/architecture'

import { DmRoomMessage } from '../DmRoomMessage'
import { DmRoomMessageSkeleton } from '../DmRoomMessageSkeleton'

/**
 * 最下部判定とスクロール動作に関する閾値定義
 */
const SCROLL_BOTTOM_THRESHOLD = 100 // 最下部判定の閾値（px）: この範囲内なら「最下部にいる」とみなす
const SCROLL_BOTTOM_OFFSET = 100 // 確実に最下部到達するための余剰スクロール（px）

export type DmRoomMessageListRef = {
  toBottom: (forceScroll?: boolean) => void
}

type DmRoomMessageListProps = {
  userId: string
  messages: DmRoomMessageBoxFragment[]
  dmRoom: DmRoomDetailQuery['dmRoom']
  loading: boolean
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
  emptyMessage: string
  refObject: RefObject<DmRoomMessageListRef>
}

export const DmRoomMessageList: Organism<DmRoomMessageListProps> = ({
  userId,
  messages,
  dmRoom,
  loading,
  hasNextPage,
  onFetchMore,
  emptyMessage,
  refObject,
}) => {
  const messageWrapperRef = useRef<HTMLDivElement>(null)
  const messageListRef = useRef<HTMLDivElement>(null)
  const bottomAnchorRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  // 初回ローディング完了時のみ最下部にスクロール
  useLayoutEffect(() => {
    if (!loading && !hasScrolledRef.current && bottomAnchorRef.current) {
      bottomAnchorRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      })
      hasScrolledRef.current = true

      // 画像読み込みによるレイアウトシフト防止処理
      const images = messageListRef.current?.querySelectorAll('img') ?? []
      // 全画像の読み込みを待つ
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) {
          return Promise.resolve()
        }
        return new Promise<void>(resolve => {
          img.onload = () => resolve()
          img.onerror = () => resolve() // エラーでも続行
        })
      })

      Promise.all(imagePromises).then(() => {
        if (bottomAnchorRef.current) {
          bottomAnchorRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'end',
          })
          hasScrolledRef.current = true
        }
      })
    }
  }, [loading])

  // 外部から渡されたrefにメソッドを公開
  useImperativeHandle(refObject, () => ({
    toBottom(forceScroll) {
      if (!messageWrapperRef.current) return
      // ユーザーが既に最下部にいるかチェック
      const isAtBottom =
        messageWrapperRef.current.scrollHeight -
          messageWrapperRef.current.scrollTop <=
        messageWrapperRef.current.clientHeight + SCROLL_BOTTOM_THRESHOLD

      // forceScrollが有効、またはユーザーが最下部にいる場合はスクロール
      if (forceScroll || isAtBottom) {
        /**
         * hack
         * rerenderを待つための遅延
         */
        setTimeout(() => {
          if (!messageWrapperRef.current) return
          messageWrapperRef.current.scrollTop =
            (messageListRef.current?.offsetHeight ?? 0) + SCROLL_BOTTOM_OFFSET
        }, 0)
      }
    },
  }))

  return (
    <Box
      ref={messageWrapperRef}
      sx={{
        /** フッター分の高さを引いた高さ */
        height: 'calc(100% - 48px)',
        overflowY: 'auto',
        ...styleSets.hideScrollbar(),
      }}
    >
      <Box ref={messageListRef} sx={{ py: 5 }}>
        <ScrollFetcher
          nodes={messages.map(message => (
            <DmRoomMessage
              key={message.id}
              userId={userId}
              dmRoom={dmRoom}
              dmRoomMessage={message}
            />
          ))}
          emptyMessage={emptyMessage}
          loading={loading}
          loadingComponent={<DmRoomMessageSkeleton />}
          hasNextPage={hasNextPage}
          onFetchMore={onFetchMore}
          inverted
          hiddenLoading
        />
        {/* 初回レンダリング時のスクロール位置を一番下にするためのアンカー */}
        <Box
          ref={bottomAnchorRef}
          sx={{ height: 0, visibility: 'hidden' }}
          aria-hidden="true"
        />
      </Box>
    </Box>
  )
}
